'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { notify } from '@org/design-system';
import type { AuthConfig } from '../types/app-config';
import {
  getStoredAuthToken,
  setStoredAuthToken,
  clearStoredAuthToken,
} from './token-storage';
import { setGlobalUnauthorizedInterceptor } from '../hooks/use-dynamic-entity';

export interface RuntimeAuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: string | null;
  permissions: string[];
  hasPermission: (permission?: string) => boolean;
  login: (token: string) => void;
  logout: () => void;
}

const RuntimeAuthContext = createContext<RuntimeAuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  auth?: AuthConfig;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = window.atob(padded);
    const parsed = JSON.parse(decoded) as unknown;
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function parseJsonToken(token: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(token) as unknown;
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function extractRoleFromPayload(payload: Record<string, unknown>): string | null {
  const directRole = payload.role;
  if (typeof directRole === 'string' && directRole.trim()) {
    return directRole.trim();
  }

  const roles = payload.roles;
  if (Array.isArray(roles)) {
    const firstRole = roles.find(role => typeof role === 'string' && role.trim());
    if (typeof firstRole === 'string') {
      return firstRole.trim();
    }
  }

  const nestedCandidates = [
    payload.user,
    payload.profile,
    payload.app_metadata,
    payload.user_metadata,
  ];

  for (const nested of nestedCandidates) {
    if (!nested || typeof nested !== 'object') continue;
    const nestedRole = (nested as Record<string, unknown>).role;
    if (typeof nestedRole === 'string' && nestedRole.trim()) {
      return nestedRole.trim();
    }
  }

  return null;
}

function roleFromToken(token: string | null, auth?: AuthConfig): string | null {
  if (!token) return null;

  const payload = decodeJwtPayload(token) || parseJsonToken(token);
  const extractedRole = payload ? extractRoleFromPayload(payload) : null;
  const allowedRoles = auth?.roles || [];

  if (extractedRole) {
    if (!allowedRoles.length || allowedRoles.includes(extractedRole)) {
      return extractedRole;
    }
  }

  if (allowedRoles.includes(token)) {
    return token;
  }

  const tokenRoleMatch = token.match(/(?:^|[;,\s])role[:=]([a-zA-Z0-9_-]+)/);
  if (tokenRoleMatch?.[1]) {
    const parsedRole = tokenRoleMatch[1];
    if (!allowedRoles.length || allowedRoles.includes(parsedRole)) {
      return parsedRole;
    }
  }

  return null;
}

function permissionMatches(requiredPermission: string, grantedPermission: string): boolean {
  if (grantedPermission === '*') return true;
  if (grantedPermission === requiredPermission) return true;

  if (grantedPermission.endsWith(':*')) {
    const prefix = grantedPermission.slice(0, -1);
    return requiredPermission.startsWith(prefix);
  }

  if (grantedPermission.endsWith('.*')) {
    const prefix = grantedPermission.slice(0, -1);
    return requiredPermission.startsWith(prefix);
  }

  return false;
}

export function AuthProvider({ children, auth }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setToken(getStoredAuthToken(auth));
    setIsLoading(false);
  }, [auth?.tokenStorageKey]);

  useEffect(() => {
    if (auth?.provider === 'none') {
      setGlobalUnauthorizedInterceptor(() => {});
      return () => setGlobalUnauthorizedInterceptor(null);
    }

    if (auth?.provider === 'custom') {
      setGlobalUnauthorizedInterceptor(({ auth: authConfig, loginUrl }) => {
        clearStoredAuthToken(authConfig);
        setToken(null);
        notify.error('Session expired');

        if (typeof window !== 'undefined' && window.location.pathname !== loginUrl) {
          window.location.assign(loginUrl);
        }
      });

      return () => setGlobalUnauthorizedInterceptor(null);
    }

    setGlobalUnauthorizedInterceptor(null);
    return () => setGlobalUnauthorizedInterceptor(null);
  }, [auth]);

  const login = useCallback((nextToken: string) => {
    const normalized = nextToken.trim();
    if (!normalized) return;
    setStoredAuthToken(normalized, auth);
    setToken(normalized);
  }, [auth]);

  const logout = useCallback(() => {
    clearStoredAuthToken(auth);
    setToken(null);
  }, [auth]);

  const role = useMemo(
    () => roleFromToken(token, auth),
    [token, auth],
  );

  const permissions = useMemo(() => {
    if (!auth?.permissions || !role) return [];
    return auth.permissions[role] || [];
  }, [auth?.permissions, role]);

  const hasPermission = useCallback((permission?: string) => {
    if (!permission) return true;

    const configuredPermissions = auth?.permissions;
    if (!configuredPermissions || Object.keys(configuredPermissions).length === 0) {
      return true;
    }

    if (!role) return false;

    return permissions.some(granted => permissionMatches(permission, granted));
  }, [auth?.permissions, role, permissions]);

  const value = useMemo<RuntimeAuthContextValue>(() => ({
    token,
    isAuthenticated: !!token,
    isLoading,
    role,
    permissions,
    hasPermission,
    login,
    logout,
  }), [token, isLoading, role, permissions, hasPermission, login, logout]);

  return (
    <RuntimeAuthContext.Provider value={value}>
      {children}
    </RuntimeAuthContext.Provider>
  );
}

export function useRuntimeAuth(): RuntimeAuthContextValue {
  const context = useContext(RuntimeAuthContext);
  if (!context) {
    throw new Error('useRuntimeAuth must be used within an AuthProvider');
  }
  return context;
}
